/**
 * API calls for the hint-quality views.
 *
 * Both endpoints resolve their own scope from the caller's token, so there is
 * no scope parameter here by design — the teacher app and the admin console
 * call exactly the same URLs and get different rows.
 */

import type { ApiClient } from '../api';
import type {
  HintQualityRollupParams,
  HintQualityRollupResponse,
  HintQualityQuestionResponse,
  HintQualitySessionParams,
  HintQualitySessionsResponse,
  HintQualityStudentParams,
  HintQualityStudentsResponse,
  HintReviewerFeedback,
} from './types';

export function buildRollupQuery(params: HintQualityRollupParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('pageSize', String(params.pageSize));
  if (params.sinceDate) q.set('sinceDate', params.sinceDate);
  if (params.subject) q.set('subject', params.subject);
  if (params.search) q.set('search', params.search);
  if (params.hasNegative) q.set('hasNegative', 'true');
  if (params.questionSetId) q.set('questionSetId', params.questionSetId);
  if (params.review) q.set('review', params.review);
  if (params.sortBy) q.set('sortBy', params.sortBy);
  if (params.sortDir) q.set('sortDir', params.sortDir);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** Both list endpoints take the same filter vocabulary, so one builder serves
 *  both — a filter added to one is spelled the same way on the other. */
export function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '' || v === false) continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function createHintQualityApi(api: ApiClient) {
  return {
    getRollup: (params: HintQualityRollupParams = {}) =>
      api.fetchApi<HintQualityRollupResponse>(`/hint-quality/questions${buildRollupQuery(params)}`),

    getSessions: (params: HintQualitySessionParams = {}) =>
      api.fetchApi<HintQualitySessionsResponse>(
        `/hint-quality/sessions${buildQuery(params as Record<string, string | number | undefined>)}`),

    getStudents: (params: HintQualityStudentParams = {}) =>
      api.fetchApi<HintQualityStudentsResponse>(
        `/hint-quality/students${buildQuery(params as Record<string, string | number | undefined>)}`),

    /** Record this reviewer's verdict on one hint. A thumbs-down also opens the
     *  existing app-feedback flag thread, server-side. */
    reviewHint: (args: {
      sessionId: string;
      entryIndex: number;
      rating: 'positive' | 'negative';
      comment?: string;
    }) =>
      api.fetchApi<{ success: boolean; reviewerFeedback: HintReviewerFeedback }>(
        '/hint-quality/review',
        { method: 'POST', body: JSON.stringify(args) },
      ),

    getQuestion: (questionId: string) =>
      api.fetchApi<HintQualityQuestionResponse>(`/hint-quality/questions/${questionId}`),
  };
}

/** Convert a "last N days" window to the ISO date the API expects. Empty
 *  string means all time — the caller omits `sinceDate` entirely. */
export function sinceDateFromRange(range: string): string | undefined {
  const days = Number(range);
  if (!days || Number.isNaN(days)) return undefined;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}
